import type { OrgBillingLockReason } from '@mfw/common';

import { OrgBillingStatus, SubscriptionStatus } from '@mfw/common';

import type { ApiBillingOverview } from '../../api/types/payments.types';

export type BillingBannerState =
  | 'pastDue'
  /**
   * Q3/Task 4: the organization is restricted because its plan was CANCELLED
   * and the paid period ran out. It owes NOTHING. A separate variant rather
   * than a reuse of `'suspended'` on purpose — that value's copy names a debt,
   * and calling a paid-up customer a debtor is the one thing this state exists
   * to prevent.
   */
  | 'subscriptionEnded'
  | 'suspended'
  | 'trial'
  | null;

/**
 * Which billing banner an organization's overview calls for.
 *
 * §16 (R-AUD-62): the org lock is decided by `OrgBillingPlan.status`, NOT by
 * the subscription — `OrgBillingLockService.isOrgLocked` reads exactly that
 * column, and `payNow` deliberately moves the subscription to past_due before
 * the charge outcome without touching the plan. Reading the subscription alone
 * left two ways for a locked organization to explain nothing at all: an
 * organization with no platform-billing subscription row (the plan is the only
 * row guaranteed to exist), and one whose subscription had not caught up. Both
 * showed a working app plus an unattributed error toast.
 *
 * So the plan decides the block and the subscription decides the rest — it is
 * the row carrying `trialEndsAt`/`graceUntil`, which the softer copy needs.
 */
export function resolveBillingBannerState(
  overview: ApiBillingOverview | null | undefined,
  options?: {
    /**
     * `organization.billingLocked` — the same `isOrgLocked` answer the plan
     * carries, but readable WITHOUT `billing_read`. It is the only lock signal a
     * client or trainer of the organization ever gets, and they are the members
     * the silent lock hit hardest.
     */
    organizationLocked?: boolean;
    /**
     * `organization.billingLockReason` — WHY, from the same row and the same
     * backend chokepoint as the boolean above, and readable without
     * `billing_read` for exactly the same reason.
     *
     * The boolean alone is a lie for half the people who see it: an
     * organization that cancelled its plan and owes nothing is locked by the
     * very same flag as one in arrears, so every member of it read "settle the
     * debt to restore access". The reason is what separates them.
     */
    organizationLockReason?: OrgBillingLockReason | null;
  },
): BillingBannerState {
  if (options?.organizationLocked) {
    // The overview is only a FALLBACK here, never the source: the people who
    // see this banner most are clients and trainers, who hold no `billing_read`
    // and never load it at all. It covers the render paths that pass the flag
    // without the reason.
    const reason =
      options.organizationLockReason ?? overview?.lock?.reason ?? null;
    return reason === 'subscription_ended' ? 'subscriptionEnded' : 'suspended';
  }
  // An ENDED plan is locked and owes nothing. Without this branch it fell
  // through to a CANCELED subscription and the banner said nothing at all.
  if (overview?.plan?.status === OrgBillingStatus.ENDED) {
    return 'subscriptionEnded';
  }
  if (overview?.plan?.status === OrgBillingStatus.SUSPENDED) return 'suspended';

  switch (overview?.subscription?.status) {
    case SubscriptionStatus.SUSPENDED:
      return 'suspended';
    case SubscriptionStatus.PAST_DUE:
    case SubscriptionStatus.GRACE:
      return 'pastDue';
    case SubscriptionStatus.TRIALING:
      return 'trial';
    default:
      return null;
  }
}
